import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  MdTrendingUp, MdShoppingCart, MdFactory, MdAttachMoney,
  MdDragIndicator, MdCalendarToday, MdDateRange,
  MdFilterAlt, MdRefresh
} from 'react-icons/md';
import { FaBoxOpen } from 'react-icons/fa';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, AreaChart, Area
} from 'recharts';

import './Dashboard.css';

/* ═══════════════════════════════════════════════════
   FULL RAW DUMMY DATA — date-tagged for filtering
═══════════════════════════════════════════════════ */

// Generate date-tagged records spanning last 12 months
const today = new Date();
const fmt = (d) => d.toISOString().split('T')[0];

const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const subDays = (d, n) => addDays(d, -n);

// Build daily data for last 365 days
const buildDailyData = () => {
  const data = [];
  for (let i = 364; i >= 0; i--) {
    const date = subDays(today, i);
    const dow = date.getDay();
    const isWeekend = dow === 0 || dow === 6;
    const base = isWeekend ? 0.4 : 1;
    data.push({
      date: fmt(date),
      sales: Math.round((Math.random() * 8 + 4) * base * 10) / 10,
      purchase: Math.round((Math.random() * 5 + 2) * base * 10) / 10,
      salesOrders: Math.floor(Math.random() * 8 + 1) * (isWeekend ? 0 : 1),
      purchaseOrders: Math.floor(Math.random() * 5 + 1) * (isWeekend ? 0 : 1),
      productionOrders: Math.floor(Math.random() * 3) * (isWeekend ? 0 : 1),
    });
  }
  return data;
};

const ALL_DAILY = buildDailyData();

// Orders raw data
const ALL_ORDERS = [
  { id: 'SO-2024-0184', date: fmt(today),               customer: 'Ramesh Kumar',  initials: 'RK', color: '#0d9488', bg: '#f0fdfa', amount: 124500, status: 'Confirmed',  statusClass: 'status-green' },
  { id: 'SO-2024-0183', date: fmt(subDays(today, 1)),   customer: 'Priya Shah',    initials: 'PS', color: '#854F0B', bg: '#FAEEDA', amount: 87200,  status: 'Pending',    statusClass: 'status-amber' },
  { id: 'SO-2024-0182', date: fmt(subDays(today, 2)),   customer: 'Arjun Mehta',   initials: 'AM', color: '#185FA5', bg: '#E6F1FB', amount: 231000, status: 'Dispatched', statusClass: 'status-blue'  },
  { id: 'SO-2024-0181', date: fmt(subDays(today, 4)),   customer: 'Neha Patel',    initials: 'NP', color: '#534AB7', bg: '#EEEDFE', amount: 56800,  status: 'Invoiced',   statusClass: 'status-green' },
  { id: 'SO-2024-0180', date: fmt(subDays(today, 5)),   customer: 'Vijay Reddy',   initials: 'VR', color: '#A32D2D', bg: '#FCEBEB', amount: 308400, status: 'Rejected',   statusClass: 'status-red'   },
  { id: 'SO-2024-0179', date: fmt(subDays(today, 8)),   customer: 'Sunita Joshi',  initials: 'SJ', color: '#0f766e', bg: '#f0fdfa', amount: 94100,  status: 'Confirmed',  statusClass: 'status-green' },
  { id: 'SO-2024-0178', date: fmt(subDays(today, 12)),  customer: 'Karan Mehta',   initials: 'KM', color: '#854F0B', bg: '#FAEEDA', amount: 142300, status: 'Invoiced',   statusClass: 'status-green' },
  { id: 'SO-2024-0177', date: fmt(subDays(today, 20)),  customer: 'Anjali Rao',    initials: 'AR', color: '#185FA5', bg: '#E6F1FB', amount: 67500,  status: 'Pending',    statusClass: 'status-amber' },
  { id: 'SO-2024-0176', date: fmt(subDays(today, 35)),  customer: 'Deepak Singh',  initials: 'DS', color: '#534AB7', bg: '#EEEDFE', amount: 189000, status: 'Dispatched', statusClass: 'status-blue'  },
  { id: 'SO-2024-0175', date: fmt(subDays(today, 60)),  customer: 'Meera Nair',    initials: 'MN', color: '#A32D2D', bg: '#FCEBEB', amount: 213400, status: 'Confirmed',  statusClass: 'status-green' },
  { id: 'SO-2024-0174', date: fmt(subDays(today, 90)),  customer: 'Ravi Sharma',   initials: 'RS', color: '#0d9488', bg: '#f0fdfa', amount: 78200,  status: 'Invoiced',   statusClass: 'status-green' },
  { id: 'SO-2024-0173', date: fmt(subDays(today, 200)), customer: 'Pooja Gupta',   initials: 'PG', color: '#854F0B', bg: '#FAEEDA', amount: 54600,  status: 'Confirmed',  statusClass: 'status-green' },
];

const ALL_PRODUCTION = [
  { id: 'PO-042', date: fmt(today),              item: 'CP-200 Pump',   qty: 50,  status: 'In Progress', statusClass: 'status-amber' },
  { id: 'PO-041', date: fmt(subDays(today, 1)),  item: 'Motor A3',      qty: 30,  status: 'Completed',   statusClass: 'status-green' },
  { id: 'PO-040', date: fmt(subDays(today, 3)),  item: 'SP-100 Pump',   qty: 20,  status: 'QC Check',    statusClass: 'status-blue'  },
  { id: 'PO-039', date: fmt(subDays(today, 6)),  item: 'Impeller Kit',  qty: 200, status: 'Planned',     statusClass: 'status-gray'  },
  { id: 'PO-038', date: fmt(subDays(today, 10)), item: 'MB-50 Pump',    qty: 40,  status: 'Completed',   statusClass: 'status-green' },
  { id: 'PO-037', date: fmt(subDays(today, 25)), item: 'Motor B5',      qty: 15,  status: 'Completed',   statusClass: 'status-green' },
  { id: 'PO-036', date: fmt(subDays(today, 45)), item: 'CP-100 Pump',   qty: 60,  status: 'Completed',   statusClass: 'status-green' },
];

const ACTIVITY_ALL = [
  { dot: '#14b8a6', text: 'Sales Invoice SI-2024-0184 created',  time: '2 min ago',  date: fmt(today)              },
  { dot: '#8b5cf6', text: 'GRN-0097 approved by Admin',          time: '18 min ago', date: fmt(today)              },
  { dot: '#f59e0b', text: 'New customer Arjun Mehta added',      time: '1 hr ago',   date: fmt(today)              },
  { dot: '#ef4444', text: 'Production order PO-042 started',     time: '3 hr ago',   date: fmt(today)              },
  { dot: '#10b981', text: 'Stock updated for Pump Motor A3',     time: '5 hr ago',   date: fmt(today)              },
  { dot: '#0d9488', text: 'Quotation QT-0063 sent to client',    time: 'Yesterday',  date: fmt(subDays(today, 1))  },
  { dot: '#8b5cf6', text: 'PO-036 material issued',              time: '3 days ago', date: fmt(subDays(today, 3))  },
  { dot: '#f59e0b', text: 'New supplier Ganesh Traders added',   time: '1 week ago', date: fmt(subDays(today, 7))  },
  { dot: '#14b8a6', text: 'Monthly invoice batch generated',     time: '2 weeks ago',date: fmt(subDays(today, 14)) },
  { dot: '#ef4444', text: 'Annual audit records uploaded',       time: '2 months ago',date: fmt(subDays(today, 60))},
];

const TOP_PRODUCTS_ALL = [
  { name: 'Centrifugal Pump CP-200', revenue: 1240000, pct: 82, color: '#14b8a6' },
  { name: 'Submersible Pump SP-100', revenue: 980000,  pct: 65, color: '#f59e0b' },
  { name: 'Monoblock Pump MB-50',    revenue: 710000,  pct: 48, color: '#8b5cf6' },
  { name: 'Motor A3 3HP',            revenue: 520000,  pct: 35, color: '#ef4444' },
  { name: 'Impeller Kit IK-10',      revenue: 360000,  pct: 24, color: '#10b981' },
];

const STOCK_DATA = [
  { name: 'Raw Material', value: 38, color: '#14b8a6' },
  { name: 'WIP',          value: 22, color: '#f59e0b' },
  { name: 'Finished',     value: 30, color: '#8b5cf6' },
  { name: 'Scrap',        value: 10, color: '#ef4444' },
];

/* ═══════════════════════════════════════════════════
   DATE FILTER HELPERS
═══════════════════════════════════════════════════ */
const FILTERS = [
  { key: 'today',  label: 'Today'  },
  { key: 'week',   label: 'Week'   },
  { key: 'month',  label: 'Month'  },
  { key: 'year',   label: 'Year'   },
  { key: 'custom', label: 'Custom' },
];

const getRange = (filter, customStart, customEnd) => {
  const t = new Date(today); t.setHours(0,0,0,0);
  switch (filter) {
    case 'today': return { start: fmt(t), end: fmt(t) };
    case 'week':  return { start: fmt(subDays(t, 6)), end: fmt(t) };
    case 'month': return { start: fmt(subDays(t, 29)), end: fmt(t) };
    case 'year':  return { start: fmt(subDays(t, 364)), end: fmt(t) };
    case 'custom': return { start: customStart || fmt(subDays(t,29)), end: customEnd || fmt(t) };
    default:      return { start: fmt(t), end: fmt(t) };
  }
};

const inRange = (dateStr, start, end) => dateStr >= start && dateStr <= end;

const aggregateByPeriod = (dailyData, start, end, filter) => {
  const rows = dailyData.filter(d => inRange(d.date, start, end));
  if (filter === 'today') return rows;
  if (filter === 'week') {
    // group by day label
    return rows.map(r => ({
      ...r,
      label: new Date(r.date).toLocaleDateString('en-IN', { weekday: 'short' })
    }));
  }
  if (filter === 'month') {
    // group by week
    const weeks = {};
    rows.forEach(r => {
      const wk = 'W' + Math.ceil((new Date(r.date) - new Date(start)) / (7 * 86400000) + 1);
      if (!weeks[wk]) weeks[wk] = { label: wk, sales: 0, purchase: 0 };
      weeks[wk].sales    += r.sales;
      weeks[wk].purchase += r.purchase;
    });
    return Object.values(weeks).map(w => ({ ...w, sales: Math.round(w.sales * 10)/10, purchase: Math.round(w.purchase * 10)/10 }));
  }
  // year — group by month
  const months = {};
  rows.forEach(r => {
    const key = new Date(r.date).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
    if (!months[key]) months[key] = { label: key, sales: 0, purchase: 0 };
    months[key].sales    += r.sales;
    months[key].purchase += r.purchase;
  });
  return Object.values(months).map(m => ({ ...m, sales: Math.round(m.sales * 10)/10, purchase: Math.round(m.purchase * 10)/10 }));
};

const sumField = (arr, field) => arr.reduce((a, b) => a + (b[field] || 0), 0);
const fmtINR  = (n) => n >= 100000 ? `₹${(n/100000).toFixed(1)}L` : n >= 1000 ? `₹${(n/1000).toFixed(1)}K` : `₹${n}`;

/* ═══════════════════════════════════════════════════
   DRAGGABLE CARD WRAPPER
═══════════════════════════════════════════════════ */
const DraggableCard = ({ id, defaultPos, className, children, style }) => {
  const [pos, setPos]       = useState(defaultPos || { x: 0, y: 0 });
  const [dragging, setDrag] = useState(false);
  const [moved, setMoved]   = useState(false);
  const ref    = useRef(null);
  const offset = useRef({ x: 0, y: 0 });

  const startDrag = useCallback((clientX, clientY) => {
    const rect = ref.current.getBoundingClientRect();
    const parent = ref.current.closest('.dash-grid-area') || document.body;
    const pr = parent.getBoundingClientRect();
    offset.current = {
      x: clientX - rect.left + pr.left,
      y: clientY - rect.top  + pr.top,
    };
    setDrag(true);
    setMoved(true);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const parent = ref.current?.closest('.dash-grid-area') || document.body;
    const pr = parent.getBoundingClientRect();

    const onMove = (e) => {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      const w  = ref.current?.offsetWidth  || 200;
      const h  = ref.current?.offsetHeight || 100;
      setPos({
        x: Math.max(0, Math.min(cx - offset.current.x, pr.width  - w)),
        y: Math.max(0, Math.min(cy - offset.current.y, pr.height - h)),
      });
    };
    const onUp = () => setDrag(false);

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend',  onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend',  onUp);
    };
  }, [dragging]);

  return (
    <div
      ref={ref}
      className={`drag-card ${className || ''} ${dragging ? 'is-dragging' : ''} ${moved ? 'is-free' : ''}`}
      style={moved ? { ...style, position: 'absolute', left: pos.x, top: pos.y, zIndex: dragging ? 1000 : 10 } : { ...style }}
    >
      {/* drag handle bar */}
      <div
        className="drag-handle"
        onMouseDown={(e) => { e.preventDefault(); startDrag(e.clientX, e.clientY); }}
        onTouchStart={(e) => { startDrag(e.touches[0].clientX, e.touches[0].clientY); }}
        title="Drag to move"
      >
        <MdDragIndicator size={16} style={{ color: 'rgba(0,0,0,0.3)' }} />
        <span className="drag-handle-label">drag</span>
      </div>
      <div className="drag-card-inner">{children}</div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   CUSTOM CHART TOOLTIP
═══════════════════════════════════════════════════ */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="ct-label">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: ₹{p.value}L
        </p>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   MAIN DASHBOARD
═══════════════════════════════════════════════════ */
const Dashboard = () => {
  const [activeFilter, setActiveFilter] = useState('today');
  const [customStart,  setCustomStart]  = useState('');
  const [customEnd,    setCustomEnd]    = useState('');
  const [showCustom,   setShowCustom]   = useState(false);

  const { start, end } = getRange(activeFilter, customStart, customEnd);

  // Filtered daily rows
  const filteredDaily = ALL_DAILY.filter(d => inRange(d.date, start, end));

  // KPI aggregates
  const totalRevenue       = sumField(filteredDaily, 'sales') + sumField(filteredDaily, 'purchase');
  const totalSalesOrders   = sumField(filteredDaily, 'salesOrders');
  const totalPurchOrders   = sumField(filteredDaily, 'purchaseOrders');
  const totalProdOrders    = sumField(filteredDaily, 'productionOrders');

  // Previous period for badge comparison
  const days   = filteredDaily.length || 1;
  const prevS  = ALL_DAILY.filter(d => {
    const dd = new Date(d.date);
    const ps = subDays(new Date(start), days);
    return d.date >= fmt(ps) && d.date < start;
  });
  const prevRev  = sumField(prevS, 'sales') + sumField(prevS, 'purchase') || 1;
  const prevSO   = sumField(prevS, 'salesOrders') || 1;
  const pctRev   = (((totalRevenue - prevRev) / prevRev) * 100).toFixed(1);
  const pctSO    = (((totalSalesOrders - prevSO) / prevSO) * 100).toFixed(1);

  const kpiCards = [
    { label: 'Total Revenue',     value: fmtINR(Math.round(totalRevenue * 100000)), badge: `${pctRev > 0 ? '+' : ''}${pctRev}%`, up: pctRev >= 0, color: '#14b8a6', bg: '#f0fdfa', icon: <MdAttachMoney size={20}/> },
    { label: 'Sales Orders',      value: totalSalesOrders,  badge: `${pctSO > 0 ? '+' : ''}${pctSO}%`, up: pctSO >= 0, color: '#f59e0b', bg: '#FAEEDA', icon: <MdTrendingUp size={20}/> },
    { label: 'Purchase Orders',   value: totalPurchOrders,  badge: null, up: true, color: '#8b5cf6', bg: '#EEEDFE', icon: <MdShoppingCart size={20}/> },
    { label: 'Production Orders', value: totalProdOrders,   badge: null, up: true, color: '#ef4444', bg: '#FCEBEB', icon: <MdFactory size={20}/> },
  ];

  // Chart data
  const chartData = aggregateByPeriod(ALL_DAILY, start, end, activeFilter)
    .map(d => ({ ...d, label: d.label || d.date?.slice(5) }));

  // Filtered tables
  const filteredOrders     = ALL_ORDERS.filter(o => inRange(o.date, start, end));
  const filteredProduction = ALL_PRODUCTION.filter(p => inRange(p.date, start, end));
  const filteredActivity   = ACTIVITY_ALL.filter(a => inRange(a.date, start, end));

  // Quick stats (filter-aware)
  const quickStats = [
    { label: 'Open Enquiries',       value: Math.max(0, Math.floor(totalSalesOrders * 0.3)),  badge: '+4',      badgeType: 'up'      },
    { label: 'Pending Quotations',   value: Math.max(0, Math.floor(totalSalesOrders * 0.15)), badge: null,      badgeType: null       },
    { label: 'DC Pending',           value: Math.max(0, Math.floor(totalPurchOrders * 0.1)),  badge: 'on time', badgeType: 'neutral' },
    { label: 'Purchase Inspections', value: Math.max(0, Math.floor(totalPurchOrders * 0.08)), badge: null,      badgeType: null       },
    { label: 'Low Stock Alerts',     value: 7,                                                 badge: 'urgent',  badgeType: 'down'    },
    { label: 'Staff Active',         value: '14/18',                                           badge: null,      badgeType: null       },
    { label: 'Overdue Payments',     value: '₹3.2L',                                           badge: '!',       badgeType: 'down'    },
  ];

  const handleFilterClick = (key) => {
    setActiveFilter(key);
    setShowCustom(key === 'custom');
  };

  const todayStr = fmt(today);

  return (
    <div className="dash-root">

      {/* ── PAGE HEADER ── */}
      <div className="dash-header">
        <div className="dash-header-left">
          <h1 className="dash-title">Dashboard</h1>
          <p className="dash-subtitle">
            {activeFilter === 'today'
              ? `Today — ${new Date(today).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`
              : `${new Date(start).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — ${new Date(end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
            }
          </p>
        </div>

        {/* ── DATE FILTER BAR ── */}
        <div className="filter-bar">
          <div className="filter-pills">
            {FILTERS.map(f => (
              <button
                key={f.key}
                className={`filter-pill ${activeFilter === f.key ? 'active' : ''}`}
                onClick={() => handleFilterClick(f.key)}
              >
                {f.key === 'today' && <MdCalendarToday size={13} />}
                {f.key === 'custom' && <MdDateRange size={13} />}
                {f.label}
              </button>
            ))}
          </div>

          {showCustom && (
            <div className="custom-range">
              <label className="range-label">From</label>
              <input
                type="date"
                className="range-input"
                value={customStart}
                max={customEnd || todayStr}
                onChange={e => setCustomStart(e.target.value)}
              />
              <label className="range-label">To</label>
              <input
                type="date"
                className="range-input"
                value={customEnd}
                min={customStart}
                max={todayStr}
                onChange={e => setCustomEnd(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      {/* NOTE: all cards are draggable — grab the handle bar at top of each card */}
      <div className="dash-grid-area">

        {/* ── KPI CARDS ── */}
        <div className="dash-kpi-grid">
          {kpiCards.map((k, i) => (
            <DraggableCard key={`kpi-${i}`} className="kpi-drag-card" style={{ animationDelay: `${i*80}ms` }}>
              <div className="kpi-top-bar" style={{ background: k.color }} />
              <div className="kpi-inner">
                <div>
                  <p className="kpi-label">{k.label}</p>
                  <p className="kpi-value">{k.value}</p>
                  <div className="kpi-sub">
                    {k.badge && <span className={`kpi-badge ${k.up ? 'badge-up' : 'badge-down'}`}>{k.badge}</span>}
                    <span className="kpi-sub-text">vs prev period</span>
                  </div>
                </div>
                <div className="kpi-icon-box" style={{ background: k.bg, color: k.color }}>
                  {k.icon}
                </div>
              </div>
            </DraggableCard>
          ))}
        </div>

        {/* ── CHARTS ROW ── */}
        <div className="dash-charts-row">

          {/* Bar Chart */}
          <DraggableCard className="chart-drag-card">
            <div className="card-head">
              <span className="card-title">Sales vs Purchase</span>
              <div className="chart-legend">
                <span className="leg-item"><span className="leg-dot" style={{ background: '#14b8a6' }} />Sales</span>
                <span className="leg-item"><span className="leg-dot" style={{ background: '#8b5cf6' }} />Purchase</span>
              </div>
            </div>
            {chartData.length === 0 ? (
              <div className="empty-chart">No data for selected period</div>
            ) : (
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={chartData} barCategoryGap="30%" barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0fdfa" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b', fontFamily: 'Montserrat' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b', fontFamily: 'Montserrat' }} axisLine={false} tickLine={false} tickFormatter={v => `₹${v}L`} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="sales"    name="Sales"    fill="#14b8a6" radius={[5,5,0,0]} />
                  <Bar dataKey="purchase" name="Purchase" fill="#8b5cf6" radius={[5,5,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </DraggableCard>

          {/* Pie Chart */}
          <DraggableCard className="pie-drag-card">
            <div className="card-head">
              <span className="card-title">Stock by Category</span>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={STOCK_DATA} cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={3} dataKey="value">
                  {STOCK_DATA.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={v => `${v}%`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pie-legend">
              {STOCK_DATA.map((s, i) => (
                <span className="pie-leg-item" key={i}>
                  <span className="pie-dot" style={{ background: s.color }} />
                  <span className="pie-leg-text">{s.name} <strong>{s.value}%</strong></span>
                </span>
              ))}
            </div>
          </DraggableCard>
        </div>

        {/* ── MIDDLE ROW ── */}
        <div className="dash-mid-row">

          {/* Recent Orders */}
          <DraggableCard className="orders-drag-card">
            <div className="card-head">
              <span className="card-title">Recent Sales Orders</span>
              <span className="count-badge">{filteredOrders.length}</span>
            </div>
            {filteredOrders.length === 0 ? (
              <div className="empty-state">No orders in this period</div>
            ) : (
              <table className="dash-table">
                <thead>
                  <tr><th>Order #</th><th>Customer</th><th>Amount</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o, i) => (
                    <tr key={i}>
                      <td className="order-id">{o.id}</td>
                      <td>
                        <div className="cust-cell">
                          <div className="cust-avatar" style={{ background: o.bg, color: o.color }}>{o.initials}</div>
                          {o.customer}
                        </div>
                      </td>
                      <td className="amount-cell">{fmtINR(o.amount)}</td>
                      <td><span className={`status-pill ${o.statusClass}`}>{o.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DraggableCard>

          {/* Activity Feed */}
          <DraggableCard className="activity-drag-card">
            <div className="card-head">
              <span className="card-title">Activity Feed</span>
              <span className="count-badge">{filteredActivity.length}</span>
            </div>
            {filteredActivity.length === 0 ? (
              <div className="empty-state">No activity in this period</div>
            ) : (
              <div className="activity-list">
                {filteredActivity.map((a, i) => (
                  <div className="activity-item" key={i}>
                    <span className="activity-dot" style={{ background: a.dot }} />
                    <div>
                      <p className="activity-text">{a.text}</p>
                      <p className="activity-time">{a.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DraggableCard>

          {/* Quick Stats */}
          <DraggableCard className="stats-drag-card">
            <div className="card-head">
              <span className="card-title">Quick Stats</span>
            </div>
            {quickStats.map((s, i) => (
              <div className="qs-row" key={i}>
                <span className="qs-label">{s.label}</span>
                <div className="qs-right">
                  <span className="qs-val">{s.value}</span>
                  {s.badge && (
                    <span className={`kpi-badge ${
                      s.badgeType === 'up' ? 'badge-up' : s.badgeType === 'down' ? 'badge-down' : 'badge-neutral'
                    }`}>{s.badge}</span>
                  )}
                </div>
              </div>
            ))}
          </DraggableCard>
        </div>

        {/* ── BOTTOM ROW ── */}
        <div className="dash-bottom-row">

          {/* Top Products */}
          <DraggableCard className="products-drag-card">
            <div className="card-head">
              <span className="card-title">Top Products by Revenue</span>
            </div>
            <div className="products-list">
              {TOP_PRODUCTS_ALL.map((p, i) => (
                <div className="product-row" key={i}>
                  <div className="product-info">
                    <span className="product-name">{p.name}</span>
                    <span className="product-rev">{fmtINR(p.revenue)}</span>
                  </div>
                  <div className="progress-track">
                    <div className="progress-fill" style={{ width: `${p.pct}%`, background: p.color }} />
                  </div>
                </div>
              ))}
            </div>
          </DraggableCard>

          {/* Production Status */}
          <DraggableCard className="production-drag-card">
            <div className="card-head">
              <span className="card-title">Production Orders</span>
              <span className="count-badge">{filteredProduction.length}</span>
            </div>
            {filteredProduction.length === 0 ? (
              <div className="empty-state">No production orders in this period</div>
            ) : (
              <table className="dash-table">
                <thead>
                  <tr><th>Order</th><th>Item</th><th>Qty</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {filteredProduction.map((p, i) => (
                    <tr key={i}>
                      <td className="order-id" style={{ color: '#ef4444' }}>{p.id}</td>
                      <td>{p.item}</td>
                      <td className="amount-cell">{p.qty}</td>
                      <td><span className={`status-pill ${p.statusClass}`}>{p.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DraggableCard>
        </div>

      </div>{/* end dash-grid-area */}
    </div>
  );
};

export default Dashboard;